import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import block from 'bem-cn';
import { useHistory, useParams } from 'react-router-dom';

import Button from 'components/Button';
import Input from 'components/Input';
import SVG from 'components/SVG';
import Spinner from 'components/Spinner/view/desktop';
import { actions } from 'features/users/redux';
import { userTypes } from '../../../../data';

import { useAgentPasswordComplexity } from 'shared/hooks/useAgentPasswordComplexity';
import infoSVG from 'shared/img/info-yellow.svg';
import './ChangePassword.scss';

const b = block('change-password-users');

const ChangePassword = () => {
  const dispatch = useDispatch();
  const { userId } = useParams();
  const { id: authUserId, role: authRole } = useSelector(state => state.auth);
  const history = useHistory();
  const locale = useSelector(state => state.locale.locale);
  const { userInfo, actionProcessing } = useSelector(state => state.users);

  const { username, role } = userInfo;

  const isCurrentUser = +userId === authUserId;
  const targetRole = isCurrentUser ? authRole : role;
  const isTargetAgent = +targetRole === 6;

  useEffect(() => {
    if (!isCurrentUser) dispatch(actions.getUserInfo({ id: userId }));
  }, [dispatch, isCurrentUser, userId]);

  const [form, setForm] = useState({
    newPassword: '',
    oldPassword: '',
    confirmNewPassword: '',
  });

  const passwordComplexity = useAgentPasswordComplexity({
    password: form.newPassword,
    locale,
  });

  const isPasswordInvalid = isTargetAgent && form.newPassword.length > 0 && !passwordComplexity.isValid;

  const disabled =
    form.confirmNewPassword !== form.newPassword ||
    form.newPassword.length === 0 ||
    (isCurrentUser && form.oldPassword.length === 0) ||
    (isTargetAgent && !passwordComplexity.isValid);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      if (disabled) return;

      dispatch(
        actions.changePassword({
          data: {
            password: form.newPassword,
            ...(isCurrentUser ? { old_password: form.oldPassword } : {}),
          },
          id: userId,
          callback: () => history.push('/users/all'),
        }),
      );
    },
    [disabled, dispatch, history, isCurrentUser, userId, form.newPassword, form.oldPassword],
  );

  const changeValue = useCallback(
    e => {
      setForm({ ...form, [e.currentTarget.name]: e.currentTarget.value?.trim() });
    },
    [form],
  );

  if (actionProcessing) return <Spinner isLoading={actionProcessing} />;

  return (
    <div className={b()}>
      <form className={b('form')} onSubmit={onSubmit}>
        <div className={b('top')}>
          <div className={b('header')}>{locale[isCurrentUser ? 'changeMyPassword' : 'changePassword']}</div>
          {!isCurrentUser && (
            <div className={b('user-block')}>
              <div className={b('user-block-left')}>
                <SVG svgProps={{ svg: userTypes[role].icon }} className={b('user-block-icon')} />
                <div className={b('user-block-info')}>
                  <span>{username}</span>
                  <div className={b('user-block-info-type', { role })}>{userTypes[role].type}</div>
                </div>
              </div>
              <div className={b('user-block-right')}>
                <span>ID:</span>
                <span>{userId}</span>
              </div>
            </div>
          )}
          <div className={b('fields')}>
            {isCurrentUser && (
              <div className={b('form', 'input-block')}>
                <span className={b('form', 'input-block-title')}>{locale.oldPassword}</span>
                <div className={b('input')}>
                  <Input
                    placeholder={locale.oldPassword}
                    withEye
                    validState={form.oldPassword.length > 0 ? 1 : 3}
                    type="password"
                    onChange={changeValue}
                    value={form.oldPassword}
                    name="oldPassword"
                  />
                </div>
              </div>
            )}
            <div className={b('form', 'input-block')}>
              <span className={b('form', 'input-block-title')}>{locale.newPassword}</span>
              <div className={b('input')}>
                <Input
                  placeholder={locale.newPassword}
                  withEye
                  validState={form.newPassword.length === 0 || isPasswordInvalid ? 3 : 1}
                  type="password"
                  onChange={changeValue}
                  value={form.newPassword}
                  name="newPassword"
                />
              </div>

              {isTargetAgent && passwordComplexity.requirementsTooltip && (
                <div className={b('password-desc')}>
                  <span>*</span>
                  {locale.passwordInfo}
                  <span
                    className={b('password-desc-info').toString()}
                    data-tooltip={passwordComplexity.requirementsTooltip}
                    tabIndex={0}>
                    <SVG svgProps={{ svg: infoSVG }} />
                  </span>
                </div>
              )}
            </div>
            <div className={b('form', 'input-block')}>
              <span className={b('form', 'input-block-title')}>{locale.confirmNewPassword}</span>
              <div className={b('input')}>
                <Input
                  placeholder={locale.confirmNewPassword}
                  withEye
                  validState={
                    form.confirmNewPassword.length > 0 && form.confirmNewPassword === form.newPassword ? 1 : 3
                  }
                  type="password"
                  onChange={changeValue}
                  value={form.confirmNewPassword}
                  name="confirmNewPassword"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={b('bottom')}>
          <Button color="full-transparent" callBack={history.goBack} text={locale.cancel} size="low" />
          <Button disabled={disabled} type="submit" size="low" text={locale.change} />
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
